import React, { useState, useEffect } from "react";
import {
  Package,
  ClipboardList,
  CheckCircle2,
  Truck,
  Send,
  ArrowRight,
  History,
} from "lucide-react";
import dayjs from "dayjs";

const SalesmanRequestModal = ({ selectedProduct }) => {
  // Form state
  const [quantity, setQuantity] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pendingList, setPendingList] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [confirmedList, setConfirmedList] = useState([]);
  const [confirmedLoading, setConfirmedLoading] = useState(false);
  const [requestList, setRequestList] = useState([]);
  const [requestLoading, setRequestLoading] = useState(false);
  const COMPANY_CODE = localStorage.getItem("companyCode");
  let API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const DEPARTMENT = localStorage.getItem("currentSelectedDepartment") || "-";
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const USEROBJECT = JSON.parse(localStorage.getItem("currentUser")) || {};
  const USER = USEROBJECT[0];
  // Fetch pending and confirmed orders on load
  useEffect(() => {
    if (!selectedProduct?.Code) return;
    setPendingLoading(true);
    setConfirmedLoading(true);
    setError("");
    // SR_ORDERINFPROCESS
    fetch(
      `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/SR_ORDERINFPROCESS/${encodeURIComponent(selectedProduct.Code)}/-`,
    )
      .then((res) => res.json())
      .then((data) => {
        setPendingList(Array.isArray(data) ? data : []);
      })
      .catch(() => setPendingList([]))
      .finally(() => setPendingLoading(false));
    // SALESMANREQUEST
    fetch(
      `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/SALESMANREQUEST/${encodeURIComponent(selectedProduct.Code)}/-`,
    )
      .then((res) => res.json())
      .then((data) => {
        setRequestList(Array.isArray(data) ? data : []);
      })
      .catch(() => setRequestList([]))
      .finally(() => setRequestLoading(false));
    // SR_CONFIRMEDORDER
    fetch(
      `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/SR_CONFIRMEDORDER/${encodeURIComponent(selectedProduct.Code)}/-`,
    )
      .then((res) => res.json())
      .then((data) => {
        setConfirmedList(Array.isArray(data) ? data : []);
      })
      .catch(() => setConfirmedList([]))
      .finally(() => setConfirmedLoading(false));
  }, [selectedProduct?.Code, COMPANY_CODE, BASE_URL]);

  // Handle POST request
  const handleSendRequest = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    // Validate
    if (!quantity || isNaN(Number(quantity)) || Number(quantity) <= 0) {
      setError("Please enter a valid quantity.");
      setLoading(false);
      return;
    }
    // Prepare payload
    const payload = [
      {
        CmpCode: COMPANY_CODE,
        mode: "ENTRY",
        code: selectedProduct?.Code || "",
        des: selectedProduct?.Description || "",
        grp: selectedProduct?.Group || "",
        qty: Number(quantity),
        reqby: USER?.sales_man || "",
        date: new Date().toISOString(),
        remarks,
        qty_Type: "R",
        ref: "",
        POREF: 0,
        curout: 0,
        deptno: DEPARTMENT,
      },
    ];
    try {
      const res = await fetch(`${BASE_URL}/api/CRMSalesmanRequest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to post request");
      setSuccess("Request posted successfully!");
      setQuantity("");
      setRemarks("");
    } catch (err) {
      setError("Failed to post request.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-50 p-2 md:p-6 ">
      {/* 1. COMPACT POSTING SECTION (Top Header Style) */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="bg-cyan-600 px-4 py-2 flex items-center gap-2">
          <span className="text-white text-[10px] font-bold uppercase tracking-widest">
            New Reorder Posting
          </span>
        </div>

        <div className="p-4 flex flex-col lg:flex-row items-center gap-6">
          {/* Item Identification */}
          <div className="flex-1 flex flex-col border-r border-slate-100 pr-6">
            <div className="flex flex-col">
              <span className="text-lg font-bold text-slate-800 truncate max-w-md">
                {selectedProduct?.Code}
              </span>
              <span className="text-sm font-medium text-slate-600 truncate max-w-md">
                {selectedProduct?.Description ||
                  "Item Description goes here..."}
              </span>
            </div>
          </div>

          {/* Form Inputs - Linear & Compact */}
          <div className="flex-[1.5] flex items-center gap-4 w-full">
            <div className="w-32">
              <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1 ml-1">
                Quantity
              </label>
              <input
                type="number"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm font-bold focus:ring-2 focus:ring-cyan-500 outline-none"
                placeholder="0.00"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="flex-grow">
              <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1 ml-1">
                Remarks
              </label>
              <input
                type="text"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                placeholder="Brief note..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                disabled={loading}
              />
            </div>
            <button
              onClick={handleSendRequest}
              className={`mt-4 bg-cyan-600 hover:bg-cyan-700 text-white font-bold px-6 py-2.5 rounded-lg shadow-md flex items-center gap-2 transition-all active:scale-95 text-xs ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
              disabled={loading}
            >
              <Send className="w-3.5 h-3.5" />
              {loading ? "Posting..." : "POST"}
            </button>
          </div>
        </div>
        {(error || success) && (
          <div className="px-4 pb-2">
            {error && (
              <div className="text-red-600 text-xs font-bold">{error}</div>
            )}
            {success && (
              <div className="text-green-600 text-xs font-bold">{success}</div>
            )}
          </div>
        )}
      </section>

      {/* 2. TRIPLE STREAM TRACKING (Lists Section) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* COLUMN A: PENDING REQUESTS (Order in Process) */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col h-[400px]">
          <div className="p-4 border-b border-slate-50 flex items-center gap-2">
            <div className="p-1.5 bg-amber-50 rounded-lg">
              <ClipboardList className="w-4 h-4 text-amber-600" />
            </div>
            <h3 className="font-bold text-slate-700 text-sm">
              Pending Requests
            </h3>
            <span className="ml-auto bg-slate-100 text-slate-500 text-[10px] px-2 py-0.5 rounded-full font-bold">
              {requestList?.length}
            </span>
          </div>

          <div className="flex-grow overflow-y-auto divide-y divide-slate-50">
            {requestLoading ? (
              <div className="flex items-center justify-center h-full text-xs text-slate-400">
                Loading...
              </div>
            ) : requestLoading.length === 0 ? (
              <div className="flex items-center justify-center h-full text-xs text-slate-400">
                No pending requests
              </div>
            ) : (
              requestList.map((item, idx) => (
                <div
                  key={idx}
                  className="p-4 hover:bg-slate-50/80 transition-colors"
                >
                  <div className="flex justify-between">
                    <span className="text-xs font-bold text-slate-800">
                      <span className="font-normal">Req Qty:</span>{" "}
                      {parseInt(item.REQQTY)}
                    </span>

                    <span className="text-[9px] text-slate-400">
                      Ref: {item?.USER} •{" "}
                      {dayjs(item?.REQDATE).isValid()
                        ? dayjs(item?.REQDATE).format("DD MMM YY")
                        : "-"}
                    </span>
                  </div>

                  <span className="text-[9px] text-slate-400">
                    Ref: {item.USER} •{" "}
                    {dayjs(item.REQDATE).isValid()
                      ? dayjs(item.REQDATE).format("DD MMM YY")
                      : "-"}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
        {/* COLUMN B: CONFIRMED ORDERS */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col h-[400px]">
          <div className="p-4 border-b border-slate-50 flex items-center gap-2">
            <div className="p-1.5 bg-emerald-50 rounded-lg">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            </div>
            <h3 className="font-bold text-slate-700 text-sm">Confirmed</h3>
          </div>
          <div className="flex-grow overflow-y-auto divide-y divide-slate-50">
            {confirmedLoading ? (
              <div className="flex items-center justify-center h-full text-xs text-slate-400">
                Loading...
              </div>
            ) : confirmedList.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center opacity-30">
                <CheckCircle2 className="w-10 h-10 mb-2" />
                <p className="text-xs font-medium">No confirmed list</p>
              </div>
            ) : (
              confirmedList.map((item, idx) => (
                <div
                  key={idx}
                  className="p-4 hover:bg-emerald-50/40 transition-colors"
                >
                  <div className="flex justify-between">
                    <span className="text-xs font-bold text-emerald-700">
                      <span className=" font-normal">Ord Qty:</span>{" "}
                      {item.POrder_Qty}
                    </span>

                    <span className="text-[9px] text-slate-400">
                      PO: {item.Pono} •{" "}
                      {dayjs(item.Order_Dt).isValid()
                        ? dayjs(item.Order_Dt).format("DD MMM YY")
                        : "-"}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        {/* COLUMN C: GOODS IN TRANSIT */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col h-[400px]">
          <div className="p-4 border-b border-slate-50 flex items-center gap-2">
            <div className="p-1.5 bg-blue-50 rounded-lg">
              <Truck className="w-4 h-4 text-blue-600" />
            </div>
            <h3 className="font-bold text-slate-700 text-sm">In Transit</h3>
          </div>
          {pendingLoading ? (
            <div className="flex items-center justify-center h-full text-xs text-slate-400">
              Loading...
            </div>
          ) : pendingList.length === 0 ? (
            <div className="flex items-center justify-center h-full text-xs text-slate-400">
              No pending requests
            </div>
          ) : (
            pendingList.map((item, idx) => (
              <div
                key={idx}
                className="p-4 hover:bg-slate-50/80 transition-colors"
              >
                <div className="flex justify-between">
                  <span className="text-xs font-bold text-slate-800">
                    <span className=" font-normal"> Req Qty:</span>{" "}
                    {item.REQQTY}
                  </span>
                  <span className="text-[10px] font-bold text-amber-500 italic">
                    Processing
                  </span>
                </div>

                <span className="text-[9px] text-slate-400">
                  Ref: {item.USER} •{" "}
                  {dayjs(item.DATE).isValid()
                    ? dayjs(item.DATE).format("DD MMM YY")
                    : "-"}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesmanRequestModal;
